import React, { useState, useEffect, useCallback } from 'react';
import { loadingImage } from '../auth';
import FastStart from './FastStart';

export default function FCLMLive({ api, site }) {
  // AROS (and the EOL calc / fused EOL/AROS dept built on it) is OLM1-only.
  // These sites have no AROS, so those rows are hidden. Display-only: the
  // backend payload and every total (Inbound/Outbound/CPLH) are unchanged.
  const NO_AROS_SITES = ['SBD3', 'SCK8'];
  const hideAros = NO_AROS_SITES.includes(site);
  // Carton plan source. SBD3/SCK8 have no LVP locked plan, so their plan comes
  // straight from ALPS (ALPS_<site> cache). Every other site (OLM1) keeps the
  // LVP plan path below, unchanged.
  const ALPS_PLAN_SITES = ['SBD3', 'SCK8'];
  const useAlpsPlan = ALPS_PLAN_SITES.includes(site);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState((() => {
    // A night shift is keyed by its start date; before 06:00 PST the active
    // night shift started YESTERDAY, so default the date back a day.
    const pac = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    if (pac.getHours() < 6) pac.setDate(pac.getDate() - 1);
    return `${pac.getFullYear()}-${String(pac.getMonth()+1).padStart(2,'0')}-${String(pac.getDate()).padStart(2,'0')}`;
  })());
  const [shiftType, setShiftType] = useState(() => {
    // Default to night after 18:45 PST or before 06:00 PST, else day
    const pac = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const mins = pac.getHours() * 60 + pac.getMinutes();
    // Flip to night 15 min before night start: OLM1 19:00, SBD3 18:30, SCK8 18:00.
    const nightFlip = site === 'SCK8' ? 17 * 60 + 45 : site === 'SBD3' ? 18 * 60 + 15 : 18 * 60 + 45;
    return (mins >= nightFlip || mins < 6 * 60) ? 'night' : 'day';
  });
  const [lastFetch, setLastFetch] = useState(null);
  const [cartonPlanInput, setCartonPlanInput] = useState('');
  const [showPartial, setShowPartial] = useState(true);
  const [lvpRates, setLvpRates] = useState({});
  const [lvpPlan, setLvpPlan] = useState(null);

  const fetchData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    const endpoint = forceRefresh ? `${api}/api/fclm/refresh` : `${api}/api/fclm/hourly?site=${site}&date=${date}&shift_type=${shiftType}`;
    const opts = forceRefresh
      ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ site, date, shift_type: shiftType }) }
      : {};
    try {
      const res = await fetch(endpoint, opts);
      const json = await res.json();
      setData(json);
      setLastFetch(json.fetched_at);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, site, date, shiftType]);

  const fetchCacheOnly = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${api}/api/fclm/cache?site=${site}&date=${date}&shift_type=${shiftType}`);
      const json = await res.json();
      if (res.ok) {
        setData(json);
        setLastFetch(json.fetched_at || 'cached');
      } else {
        alert(json.error || 'No cached data found for this date/shift.');
      }
    } catch (e) { console.error(e); alert('Failed to fetch cached data.'); }
    setLoading(false);
  }, [api, site, date, shiftType]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const interval = setInterval(() => { if (new Date().getMinutes() === 15) fetchData(true); }, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Fetch LVP rates for this shift
  useEffect(() => {
    if (useAlpsPlan) {
      // ALPS plan for this date + shift (site-scoped cache; read-only).
      // IB/OB cartons = CAPABILITY_COMBINED_CARTONS (= show hrs x CPLH), PID =
      // IB cartons x ALPS PID share of assignments. ALPS has no function-level
      // plan, so computedHours is empty and per-function plan columns stay '—'.
      const alpsShift = shiftType === 'night' ? 'NIGHT_SHIFT' : 'DAY_SHIFT';
      setLvpRates({});
      fetch(`${api}/api/fclm/cache?site=${site}&date=ALPS_${site}&shift_type=data`)
        .then(r => (r.ok ? r.json() : null))
        .then(alps => {
          const rows = (alps && alps.rows) || [];
          const g = (mp, concept) => {
            const r = rows.find(x => x.date === date && x.shift === alpsShift && x.mainProcess === mp && x.concept === concept && x.aspect === 'FORECAST');
            return r && typeof r.value === 'number' ? r.value : null;
          };
          const ib = g('INBOUND', 'CAPABILITY_COMBINED_CARTONS');
          const ob = g('DA', 'CAPABILITY_COMBINED_CARTONS');
          if (!ib && !ob) { setLvpPlan(null); return; }
          const asgComb = g('INBOUND', 'ASSIGNMENT_COMBINED_CARTONS');
          const asgPid = g('INBOUND', 'ASSIGNMENT_PID_CARTONS');
          const pidShare = asgComb > 0 && asgPid != null ? Math.min(1, asgPid / asgComb) : null;
          const cartons = { inbound: Math.round(ib || 0), outbound: Math.round(ob || 0) };
          if (ib && pidShare != null) cartons.pid = Math.round(ib * pidShare);
          setLvpPlan({
            source: 'alps',
            shiftKey: alpsShift,
            computedHours: {},
            cartons,
            totalCartons: Math.round(ib || 0),
            ibHours: g('INBOUND', 'SHOW_HOURS') || 0,
            daHours: g('DA', 'SHOW_HOURS') || 0,
            adminHours: 0,
            ibCplh: g('INBOUND', 'CPLH'),
            daCplh: g('DA', 'CPLH'),
          });
        }).catch(() => setLvpPlan(null));
      return;
    }
    const d = new Date(date + 'T12:00:00');
    const day = d.getDay();
    const sunday = new Date(d); sunday.setDate(d.getDate() - day);
    const weekStart = `${sunday.getFullYear()}-${String(sunday.getMonth()+1).padStart(2,'0')}-${String(sunday.getDate()).padStart(2,'0')}`;
    const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const shiftKey = `${dayNames[day]}_${shiftType}`;
    fetch(`${api}/api/fclm/cache?site=${site}&date=LVP_${weekStart}&shift_type=plan`)
      .then(r => r.json())
      .then(plan => {
        if (!plan) return;
        const rates = {};
        for (const [fn, val] of Object.entries(plan.overrides || {})) {
          if (val?.rate?.[shiftKey]) rates[fn] = parseFloat(val.rate[shiftKey]) || 0;
        }
        setLvpRates(rates);
        // Planned cartons per bucket + computed hours → drives per-function/dept plan rates
        const sd = plan.shiftData?.[shiftKey] || {};
        const fpp = plan.fppRatios || {};
        const total = sd.cartons || 0;
        if (total > 0) {
          const eolPct = (plan.eolPct || 33) / 100;
          const pid = sd.pidCartons ? parseInt(sd.pidCartons) : Math.round(total * (fpp.pid || 0));
          const aros = sd.arosCartons ? parseInt(sd.arosCartons) : Math.round(total * (fpp.aros || 0));
          const ibpr = Math.round(total * (fpp.pr || 0));   // IB PR cartons
          const skid = Math.round(total * (fpp.skid || 0)); // SKID cartons
          const pr = ibpr + skid;                           // PR goal = IB PR + SKID
          const pit = pr;                                   // PIT goal matches PR goal
          const eol = Math.round(pid * eolPct);
          const fluidDirect = Math.round(pid * (1 - eolPct));   // PID total * (1 - EOL%)
          const fluidOffline = Math.max(0, pid + aros - fluidDirect); // PID + AROS - Fluid Direct
          setLvpPlan({
            shiftKey,
            computedHours: plan.computedHours || {},
            cartons: { pid, aros, pr, pit, eol, fluidOffline, fluidDirect, inbound: total, outbound: total },
            totalCartons: total,
            ibHours: parseFloat(plan.ibHours?.[shiftKey]) || 0,
            daHours: parseFloat(plan.daHours?.[shiftKey]) || 0,
            adminHours: parseFloat(plan.adminHours?.[shiftKey]) || 0,
          });
        } else setLvpPlan(null);
      }).catch(() => {});
  }, [api, site, date, shiftType, useAlpsPlan]);

  const getHours = (path) => {
    if (!data || !data.labor) return {};
    const row = data.labor.find(r => r.process_path === path);
    return row ? row.hours : {};
  };
  const getTotal = (path) => {
    if (!data || !data.labor) return 0;
    const row = data.labor.find(r => r.process_path === path);
    return row ? row.total : 0;
  };

  // Format CPLH: <100 → 1 decimal, >=100 → whole number
  const fmtCplh = (v) => v >= 100 ? Math.round(v) : v.toFixed(1);
  // Add asterisk if TOT issue for that hour
  const totFlag = (hourLabel) => data?.tot?.[hourLabel] ? '*' : '';

  // Projected finish based on completed BRT hours only (exclude current partial hour)
  const getProjected = () => {
    if (!data || !data.schedule || !data.hour_labels) return null;
    const now = new Date();
    const currentHour = now.getHours();
    const currentMin = now.getMinutes();
    let completedBrt = 0;
    let completedCartons = 0;
    let completedHrs = 0;
    for (let i = 0; i < (data.hour_labels?.length || 0); i++) {
      const hh = parseInt((data.hour_labels[i] || '').split(':')[0], 10);
      // Only count fully completed hours
      // After midnight, the evening part of the night shift is complete. SBD3/SCK8
      // nights start at 18:00/18:30, so their evening hours are >= 18 (OLM1 unchanged).
      const eveningDone = useAlpsPlan ? (shiftType === 'night' && hh >= 18 && currentHour < 6) : (shiftType === 'night' && hh > 19 && currentHour < 6);
      if (hh < currentHour || eveningDone) {
        completedBrt += data.schedule[i]?.brt || 0;
        completedCartons += (data.cartons?.inbound?.hours?.[data.hour_labels[i]] || 0) + (data.cartons?.outbound?.hours?.[data.hour_labels[i]] || 0);
        completedHrs += (data.ppr_hours?.fcSummary?.[data.hour_labels[i]] || 0);
      }
    }
    const totalBrt = data.total_brt || 525;
    const remainingBrt = totalBrt - completedBrt;
    if (completedBrt === 0) return null;
    const ratePerBrt = completedCartons / completedBrt;
    const projectedCartons = completedCartons + (ratePerBrt * remainingBrt);
    const avgHrsPerHour = completedHrs / (completedBrt / 60);
    const remainingHrs = avgHrsPerHour * (remainingBrt / 60);
    const projectedHrs = completedHrs + remainingHrs;
    const projectedCplh = projectedHrs > 0 ? projectedCartons / projectedHrs : 0;
    return { projectedCartons: Math.round(projectedCartons), projectedCplh, completedBrt, totalBrt };
  };
  const projected = data ? getProjected() : null;

  const brtColumns = [
    { label: 'Total Outbound', key: 'outbound' },
    { label: 'Total Inbound', key: 'inbound' },
    { label: 'PID Total', key: 'pid' },
    { label: 'AROS Total', key: 'aros' },
    { label: 'PR Cartons', key: 'pr' },
    { label: 'Fluid Direct', key: 'fluidDirect' },
    { label: 'Fluid Offline', key: 'fluidOffline' },
    { label: 'PIT Cartons', key: 'pit' },
    { label: 'EOL (calc)', key: 'eol' },
  ].filter(c => !(hideAros && (c.key === 'aros' || c.key === 'eol')));

  // Load shift plans for planned cartons
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  useEffect(() => {
    fetch(`${api}/api/shift-plans`).then(r => r.json()).then(setPlans).catch(() => {});
  }, [api]);
  // Auto-select plan matching current date/shift
  useEffect(() => {
    const match = plans.find(p => p.date === date && p.shift_type === shiftType);
    if (match) setSelectedPlan(String(match.id));
    else setSelectedPlan('');
  }, [date, shiftType, plans]);

  // Calculate planned cartons per hour using BRT ratio.
  // Source of truth is the LVP plan (lvpPlan.cartons, which reflects live LVP
  // edits by path). Falls back to the legacy shift-plan volumes only if no LVP.
  const getPlannedCartons = (planVolumes, key, hourIndex) => {
    if (!data || !data.schedule) return 0;
    const totalBRT = data.schedule?.reduce((s, h) => s + h.brt, 0);
    const hourBRT = data.schedule?.[hourIndex]?.brt || 0;
    const ratio = totalBRT > 0 ? hourBRT / totalBRT : 0;

    // Prefer LVP: its cartons object is keyed exactly like brtColumns.
    if (lvpPlan && lvpPlan.cartons && lvpPlan.cartons[key] != null) {
      return Math.round((lvpPlan.cartons[key] || 0) * ratio);
    }
    // ALPS-planned sites: buckets ALPS doesn't plan show no plan ('—').
    if (lvpPlan && lvpPlan.source === 'alps') return 0;

    // Legacy fallback (old shift-plan volumes).
    if (!planVolumes) return 0;
    const getVol = (k, idx) => {
      const byKey = planVolumes.find(v => v.key === k);
      if (byKey) return byKey.volume || 0;
      return planVolumes[idx]?.volume || 0;
    };
    let totalVol = 0;
    if (key === 'pid') totalVol = getVol('pid', 0);
    else if (key === 'aros') totalVol = getVol('aros', 1);
    else if (key === 'pr') totalVol = getVol('pallet', 2);
    else if (key === 'inbound' || key === 'outbound') totalVol = planVolumes.reduce((s, pv) => s + (pv.volume || 0), 0);
    else if (key === 'fluidDirect') totalVol = getVol('pid', 0) * 0.75;
    else if (key === 'fluidOffline') totalVol = getVol('pid', 0) * 0.25 + getVol('aros', 1);
    else if (key === 'pit') totalVol = getVol('pallet', 2);
    else if (key === 'eol') totalVol = getVol('pid', 0) * 0.25;
    return Math.round(totalVol * ratio);
  };

  const selectedPlanData = plans.find(p => p.id === parseInt(selectedPlan));

  const ibPaths = ['PID Unload', 'PID Divert', 'PID Reinject', 'PID Operator', 'AROS Unload', 'AROS Operator', 'AROS CSX Label', 'PR Unload', 'Pallet Build', 'Wrap/Lantech', 'PR Waterspider', 'IB Pallet EPJ WS', 'PIT Unloader', 'PIT Pallet Build'].filter(p => !(hideAros && p.startsWith('AROS ')));
  const obPaths = ['Sorter Wallbuild', 'Jam Clear', 'Fluid WS', 'Fluid Load', 'Offline Wallbuild', 'EOL Avery', 'EOL Divert', 'EOL Sort', 'EOL Waterspider', 'AROS Avery', 'AROS Divert', 'AROS Waterspider', 'OB EPJ WaterSpider', 'Pallet Loader', 'OB EPJ Operator'].filter(p => !(hideAros && p.startsWith('AROS ')));
  // Display-only relabels for the function tables (data keys/paths unchanged).
  const PATH_LABELS = { 'EOL Sort': 'IDRT Sort' };
  const ibIndirectPaths = ['IB Clerk', 'IB TDR', 'IB Problem Solve', 'IB Lead/PA'];
  const obIndirectPaths = ['Ship Clerk', 'OB TDR', 'DA Problem Solve', 'DA Lead/PA', 'OB Training', 'OB Ambassador', 'Trans Out Overflow'];
  const indirectPaths = [...ibIndirectPaths, ...obIndirectPaths];

  // Map each process path to its carton source
  const pathCartonMap = {
    'PID Unload': 'pid', 'PID Divert': 'pid', 'PID Reinject': 'pid', 'PID Operator': 'pid',
    'AROS Unload': 'aros', 'AROS Operator': 'aros', 'AROS CSX Label': 'aros',
    'AROS Avery': 'aros', 'AROS Divert': 'aros', 'AROS Sort': 'aros', 'AROS Waterspider': 'aros',
    'PR Unload': 'pr', 'Pallet Build': 'pr', 'Wrap/Lantech': 'pr', 'PR Waterspider': 'pr',
    'IB Pallet EPJ WS': 'pr', 'Pallet Auditor': 'pr', 'EPJ Spotter': 'pr',
    'PIT Unloader': 'pr', 'PIT Pallet Build': 'pr', 'IB PIT Waterspider': 'pr',
    'Sorter Wallbuild': 'fluidDirect', 'Jam Clear': 'pid',
    'Fluid WS': 'fluidOffline', 'Fluid Load': 'fluidOffline', 'Offline Wallbuild': 'fluidOffline',
    'EOL Avery': 'eol', 'EOL Divert': 'eol', 'EOL Sort': 'eol', 'EOL Waterspider': 'eol', 'Re-inject': 'eol',
    'OB EPJ WaterSpider': 'pit', 'Pallet Loader': 'pit', 'OB PIT Operator': 'pit', 'OB EPJ Operator': 'pit',
  };

  // Per-path LP rates for background highlighting
  const pathLPRates = {
    'PID Unload': 236.0, 'PID Divert': 480.0, 'PID Reinject': 4688, 'PID Operator': 153.1,
    'AROS Unload': 350.6, 'AROS Operator': 721.5, 'AROS CSX Label': 361.6,
    'AROS Avery': 302.3, 'AROS Divert': 332.3, 'AROS Sort': 147.7, 'AROS Waterspider': 227.7,
    'PR Unload': 171.0, 'Pallet Build': 106.8, 'Wrap/Lantech': 200.4, 'PR Waterspider': 260.2,
    'IB Pallet EPJ WS': 284.7, 'PIT Unloader': 16493,
    'Sorter Wallbuild': 78.0, 'Jam Clear': 459.6, 'Fluid WS': 126.4, 'Fluid Load': 126.4, 'Offline Wallbuild': 66.1,
    'EOL Avery': 237.0, 'EOL Divert': 481.1, 'EOL Sort': 121.9, 'EOL Waterspider': 157.7,
    'OB EPJ WaterSpider': 284.7, 'Pallet Loader': 1227, 'OB EPJ Operator': 2220,
  };

  // FCLM process-path name -> LVP computedHours key (only the mismatches; same name otherwise)
  const FCLM_TO_LVP = {
    'Wrap/Lantech': 'Pallet Wrap/Lantech', 'Pallet Auditor': 'Pallet Auditors',
    'Sorter Wallbuild': 'Sorter Wallbuilder', 'Offline Wallbuild': 'Wall Builder', 'Re-inject': 'Trans Out Overflow',
    'EPJ Spotter': 'IB Pallet EPJ WS',
  };
  // LVP plan rate for a function = planned cartons for its path / planned (computed) hours for the shift
  const lvpPathRate = (path) => {
    if (!lvpPlan) return 0;
    const bucket = pathCartonMap[path];
    const key = FCLM_TO_LVP[path] || path;
    const h = lvpPlan.computedHours?.[key]?.[lvpPlan.shiftKey] || 0;
    const c = lvpPlan.cartons?.[bucket] || 0;
    return (h > 0 && c > 0) ? c / h : 0;
  };

  // LVP planned hours for a function path (mirrors lvpPathRate's key mapping)
  const lvpPathHours = (path) => {
    if (!lvpPlan?.computedHours) return 0;
    const key = FCLM_TO_LVP[path] || path;
    return lvpPlan.computedHours?.[key]?.[lvpPlan.shiftKey] || 0;
  };
  // LVP planned hours for a Department Throughput dept label
  const lvpDeptHours = (label) => {
    if (lvpPlan && lvpPlan.source === 'alps') {
      if (label === 'Inbound') return lvpPlan.ibHours || 0;
      if (label === 'Outbound') return lvpPlan.daHours || 0;
      if (label === 'Direct CPLH') return (lvpPlan.ibHours || 0) + (lvpPlan.daHours || 0);
      return 0;
    }
    if (!lvpPlan?.computedHours) return 0;
    const H = (k) => lvpPlan.computedHours?.[k]?.[lvpPlan.shiftKey] || 0;
    // Sum a list of per-function LVP computedHours keys (LVP emits per-casual-fn
    // keys as well as per-dept keys). Used for the fused/split depts whose LVP
    // dept-name key doesn't match the reporting label.
    const sumFns = (fns) => fns.reduce((s, f) => s + H(f), 0);
    // AROS Inbound = IB AROS functions only (Unload/Operator/CSX Label). LVP's
    // 'AROS' dept key still includes OB AROS, so sum the IB functions directly.
    if (label === 'AROS Inbound') return sumFns(['AROS Unload', 'AROS Operator', 'AROS CSX Label']);
    // Fused EOL/AROS planned hours = EOL functions + OB AROS functions (planning
    // keeps EOL Sort and AROS Sort as distinct functions; this sums them for the
    // fused reporting dept). Precise, from per-function LVP hours.
    // NOTE: LVP renamed EOL Sort -> "IDRT EOL" and AROS Sort -> "IDRT AROS"
    // (synthetic keys), so the Sort hours are saved under the IDRT keys. Sum
    // both the IDRT and legacy Sort names so neither an old nor new plan drops
    // the two largest lines (this was undercounting EOL/AROS hours by ~68/shift).
    if (label === 'EOL/AROS') return sumFns(['EOL Avery', 'EOL Divert', 'EOL Sort', 'IDRT EOL', 'EOL Waterspider', 'AROS Avery', 'AROS Divert', 'AROS Sort', 'IDRT AROS', 'AROS Waterspider']);
    const map = { 'PID Dept': 'PID', 'PR Dept': 'IB PR', 'Fluid Total': 'Fluid', 'PIT Total': 'IB PIT' };
    if (map[label]) return H(map[label]);
    // Direct = hands-on functions only; Support = indirect; Diluted = direct + support.
    // AROS is split: IB AROS (Unload/Operator/CSX Label) counts on the inbound side;
    // OB AROS (Avery/Divert/Sort/Waterspider) counts on the outbound side (it's part
    // of the fused EOL/AROS dept). Sum by function so Inbound/Outbound/Direct/Diluted
    // stay consistent with the IB/OB split and don't double-count or drop OB AROS.
    const ibArosFns = ['AROS Unload', 'AROS Operator', 'AROS CSX Label'];
    const obArosFns = ['AROS Avery', 'AROS Divert', 'AROS Sort', 'AROS Waterspider'];
    const ibDirect = ['PID', 'IB PR', 'IB PIT'];
    const obDirect = ['Sorter EOL', 'Fluid', 'OBPR'];
    const supportKeys = ['IB Support', 'OB Support'];
    const sum = (ks) => ks.reduce((s, k) => s + H(k), 0);
    const ibDirectHrs = sum(ibDirect) + sum(ibArosFns);
    const obDirectHrs = sum(obDirect) + sum(obArosFns);
    const directHrs = ibDirectHrs + obDirectHrs;
    if (label === 'Inbound') return ibDirectHrs + H('IB Support');
    if (label === 'Outbound') return obDirectHrs + H('OB Support');
    if (label === 'Direct CPLH') return directHrs;                    // direct only
    if (label === 'Diluted CPLH') return directHrs + sum(supportKeys); // direct + support
    return 0; // no clean LVP mapping
  };

  const getHourCellColor = (path, hourIndex) => {
    if (!data?.schedule) return undefined;
    const sched = data.schedule?.[hourIndex];
    if (!sched || !sched.paid) return undefined;
    const rate = lvpPathRate(path) || pathLPRates[path];
    if (!rate) return undefined;
    const hrs = getHours(path)[data.hour_labels?.[hourIndex] || ""] || 0;
    if (!hrs) return undefined;
    const cartonKey = pathCartonMap[path];
    if (!cartonKey || !data.cartons[cartonKey]) return undefined;
    const hourCartons = data.cartons?.[cartonKey]?.hours?.[data.hour_labels[hourIndex]] || 0;
    if (!hourCartons) return undefined;
    const actualCPLH = hourCartons / hrs;
    const lpTarget = ((sched.brt * 1.14286) / sched.paid) * rate;
    if (!lpTarget) return undefined;
    const pct = actualCPLH / lpTarget;
    if (pct >= 1.0) return '#e8f5e9'; // green - at or above
    if (pct >= 0.95) return undefined; // no highlight - 95-100%
    return '#ffebee'; // red - below 95%
  };

  const HoursAsRows = ({ title, paths, hourLabels, style }) => {
    const colStyle = { textAlign: 'right', padding: '4px 5px', fontSize: '0.72rem' };
    const headerStyle = { ...colStyle, fontWeight: 600, borderBottom: '1px solid #ccc' };

    const getCartonForPath = (path, h) => {
      // IDRT Sort (data path 'EOL Sort') is rated on EOL + AROS cartons combined
      // (its labor covers both the EOL and AROS manual-sort streams).
      if (path === 'EOL Sort') return (data.cartons?.eol?.hours?.[h] || 0) + (data.cartons?.aros?.hours?.[h] || 0);
      const key = pathCartonMap[path];
      if (!key || !data.cartons[key]) return 0;
      return data.cartons?.[key]?.hours?.[h] || 0;
    };
    const getTotalCartonForPath = (path) => {
      if (path === 'EOL Sort') return (data.cartons?.eol?.total || 0) + (data.cartons?.aros?.total || 0);
      const key = pathCartonMap[path];
      if (!key || !data.cartons[key]) return 0;
      return data.cartons?.[key]?.total || 0;
    };

    return (
      <div style={{ marginBottom: 24, overflowX: 'auto', ...style }}>
        <h3 className="section-title">{title}</h3>
        <table style={{ fontSize: '0.72rem', whiteSpace: 'nowrap', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1 }}></th>
              {paths.map(p => (
                <th key={p} colSpan={2} style={{ ...headerStyle, textAlign: 'center', borderLeft: '1px solid #ddd' }}>{PATH_LABELS[p] || p}</th>
              ))}
            </tr>
            <tr>
              <th style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1 }}></th>
              {paths.map(p => (
                <React.Fragment key={p}>
                  <th style={{ ...headerStyle, borderLeft: '1px solid #ddd' }}>Rate</th>
                  <th style={headerStyle}>Hours</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 700, background: '#f5f8ff', borderBottom: '2px solid #232f3e' }}>
              <td style={{ position: 'sticky', left: 0, background: '#f5f8ff', zIndex: 1, padding: '4px 8px' }}>Plan</td>
              {paths.map(p => {
                const pr = lvpPathRate(p);
                const ph = lvpPathHours(p);
                return (
                  <React.Fragment key={p}>
                    <td style={{ ...colStyle, borderLeft: '1px solid #eee', color: '#283593' }}>{pr ? Math.round(pr) : '—'}</td>
                    <td style={{ ...colStyle, color: '#283593' }}>{ph ? ph.toFixed(1) : '—'}</td>
                  </React.Fragment>
                );
              })}
            </tr>
            {hourLabels.map((h, hi) => {
              return (
                <tr key={h}>
                  <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, fontWeight: 500, padding: '4px 8px' }}>{h}</td>
                  {paths.map(p => {
                    const hrs = getHours(p)[h] || 0;
                    const pathCartons = getCartonForPath(p, h);
                    const rate = hrs > 0 && pathCartons > 0 ? Math.round(pathCartons / hrs) : 0;
                    const bgColor = getHourCellColor(p, hi);
                    return (
                      <React.Fragment key={p}>
                        <td style={{ ...colStyle, borderLeft: '1px solid #eee', color: '#555', background: bgColor }}>{rate || '—'}</td>
                        <td style={{ ...colStyle, background: bgColor }}>{hrs ? hrs.toFixed(1) : '—'}</td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
            <tr style={{ fontWeight: 700, borderTop: '2px solid #232f3e' }}>
              <td style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1, padding: '4px 8px' }}>Total</td>
              {paths.map(p => {
                const total = getTotal(p);
                const pathCartons = getTotalCartonForPath(p);
                const rate = total > 0 && pathCartons > 0 ? Math.round(pathCartons / total) : 0;
                return (
                  <React.Fragment key={p}>
                    <td style={{ ...colStyle, borderLeft: '1px solid #eee', color: '#555' }}>{rate || '—'}</td>
                    <td style={colStyle}>{total ? total.toFixed(1) : '—'}</td>
                  </React.Fragment>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const BRTTable = ({ hourLabels }) => {
    const colStyle = { textAlign: 'right', padding: '4px 6px', fontSize: '0.72rem' };
    const headerStyle = { ...colStyle, fontWeight: 600, fontSize: '0.7rem', whiteSpace: 'nowrap' };
    // Plan columns render when we have EITHER a legacy shift plan OR an LVP plan.
    // getPlannedCartons prefers LVP; the [] keeps the plan/actual/delta layout on
    // when only LVP exists (empty array is truthy but unused by the LVP path).
    const planVols = selectedPlanData?.volumes || (lvpPlan?.cartons ? [] : null);

    return (
      <div style={{ marginBottom: 24, overflowX: 'auto' }}>
        <h3 className="section-title">Carton Throughput (BRT)</h3>
        {!planVols && <p style={{ fontSize: '0.75rem', color: '#666', marginBottom: 8 }}>No shift plan or LVP for this date — planned values unavailable</p>}
        <table style={{ fontSize: '0.72rem', whiteSpace: 'nowrap', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1 }}></th>
              {brtColumns.map(c => (
                <React.Fragment key={c.key}>
                  <th colSpan={planVols ? 3 : 1} style={{ ...headerStyle, textAlign: 'center', borderLeft: '1px solid #ddd' }}>{c.label}</th>
                  {c.key === 'pr' && <th style={{ ...headerStyle, textAlign: 'center', borderLeft: '1px solid #ddd' }}>CPP</th>}
                </React.Fragment>
              ))}
              <th style={{ ...headerStyle, textAlign: 'center', borderLeft: '1px solid #ddd' }}>% Vol to<br/>Direct Doors</th>
            </tr>
            {planVols && (
              <tr>
                <th style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1 }}></th>
                {brtColumns.map(c => (
                  <React.Fragment key={c.key}>
                    <th style={{ ...headerStyle, borderLeft: '1px solid #ddd' }}>Plan</th>
                    <th style={headerStyle}>Actual</th>
                    <th style={headerStyle}>Delta</th>
                    {c.key === 'pr' && <th style={{ ...headerStyle, borderLeft: '1px solid #ddd' }}>c/plt</th>}
                  </React.Fragment>
                ))}
                <th style={{ ...headerStyle, borderLeft: '1px solid #ddd' }}>%</th>
              </tr>
            )}
          </thead>
          <tbody>
            {hourLabels.map((h, hi) => (
              <tr key={h}>
                <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, fontWeight: 500, padding: '4px 8px' }}>{h}</td>
                {brtColumns.map(c => {
                  const actual = data.cartons[c.key]?.hours[h] || 0;
                  const cppCell = c.key === 'pr' ? (() => { const prc = data.cartons?.pr?.hours?.[h] || 0; const plt = data.cartons?.pallets?.hours?.[h] || 0; return <td key="cpp" style={{ ...colStyle, borderLeft: '1px solid #ddd' }}>{plt > 0 ? (prc/plt).toFixed(1) : '—'}</td>; })() : null;
                  if (!planVols) return <React.Fragment key={c.key}><td style={{ ...colStyle, borderLeft: '1px solid #eee' }}>{actual ? Math.round(actual).toLocaleString() : '—'}</td>{cppCell}</React.Fragment>;
                  const plan = getPlannedCartons(planVols, c.key, hi);
                  const delta = actual - plan;
                  // Match the function tables' colors exactly.
                  const bg = (!actual || !plan) ? undefined : actual >= plan ? '#e8f5e9' : actual >= plan * 0.95 ? undefined : '#ffebee';
                  return (
                    <React.Fragment key={c.key}>
                      <td style={{ ...colStyle, borderLeft: '1px solid #eee', color: '#666', background: bg }}>{plan ? plan.toLocaleString() : '—'}</td>
                      <td style={{ ...colStyle, background: bg }}>{actual ? Math.round(actual).toLocaleString() : '—'}</td>
                      <td style={{ ...colStyle, background: bg, color: delta >= 0 ? '#2e7d32' : '#c62828' }}>{actual ? (delta >= 0 ? '+' : '') + Math.round(delta).toLocaleString() : '—'}</td>
                      {cppCell}
                    </React.Fragment>
                  );
                })}
                {(() => { const fd = data.cartons?.fluidDirect?.hours?.[h] || 0; const pidT = data.cartons?.pid?.hours?.[h] || 0; const pct = pidT > 0 ? Math.round(fd/pidT*100) : null; return <td style={{ ...colStyle, borderLeft: '1px solid #ddd' }}>{pct != null ? `${pct}%` : '—'}</td>; })()}
              </tr>
            ))}
            <tr style={{ fontWeight: 700, borderTop: '2px solid #232f3e' }}>
              <td style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1, padding: '4px 8px' }}>Plan vs Actual (Current)</td>
              {brtColumns.map(c => {
                const actual = data.cartons[c.key]?.total || 0;
                const cppCell = c.key === 'pr' ? (() => { const prc = data.cartons?.pr?.total || 0; const plt = data.cartons?.pallets?.total || 0; return <td key="cpp" style={{ ...colStyle, borderLeft: '1px solid #ddd', fontWeight: 700 }}>{plt > 0 ? (prc/plt).toFixed(1) : '—'}</td>; })() : null;
                if (!planVols) return <React.Fragment key={c.key}><td style={{ ...colStyle, borderLeft: '1px solid #eee' }}>{actual ? Math.round(actual).toLocaleString() : '—'}</td>{cppCell}</React.Fragment>;
                // Only count plan for hours that have actual data
                const planTotal = hourLabels.reduce((s, _, i) => {
                  const hourActual = data.cartons[c.key]?.hours[hourLabels[i]] || 0;
                  return s + (hourActual > 0 ? getPlannedCartons(planVols, c.key, i) : 0);
                }, 0);
                const delta = actual - planTotal;
                return (
                  <React.Fragment key={c.key}>
                    <td style={{ ...colStyle, borderLeft: '1px solid #eee', color: '#666' }}>{planTotal ? planTotal.toLocaleString() : '—'}</td>
                    <td style={colStyle}>{actual ? Math.round(actual).toLocaleString() : '—'}</td>
                    <td style={{ ...colStyle, color: delta >= 0 ? '#2e7d32' : '#c62828' }}>{actual ? (delta >= 0 ? '+' : '') + Math.round(delta).toLocaleString() : '—'}</td>
                    {cppCell}
                  </React.Fragment>
                );
              })}
              {(() => { const fd = data.cartons?.fluidDirect?.total || 0; const pidT = data.cartons?.pid?.total || 0; const pct = pidT > 0 ? Math.round(fd/pidT*100) : null; return <td style={{ ...colStyle, borderLeft: '1px solid #ddd', fontWeight: 700 }}>{pct != null ? `${pct}%` : '—'}</td>; })()}
            </tr>
            {projected && (
              <tr style={{ background: '#e3f2fd', fontWeight: 500 }}>
                <td style={{ ...colStyle, textAlign: 'left', fontStyle: 'italic' }}>Plan vs Proj. Actual (Total)</td>
                {brtColumns.map(c => {
                  // Only use completed full hours for rate calculation
                  const now = new Date();
                  const currentHour = now.getHours();
                  let completedCarts = 0;
                  for (let i = 0; i < hourLabels.length; i++) {
                    const [hh] = hourLabels[i].split(':').map(Number);
                    if (hh < currentHour || (useAlpsPlan && shiftType === 'night' && hh >= 18 && currentHour < 6)) {
                      completedCarts += data.cartons?.[c.key]?.hours?.[hourLabels[i]] || 0;
                    }
                  }
                  const projCpp = c.key === 'pr' ? <td key="cpp" style={{ ...colStyle, borderLeft: '1px solid #eee' }}>—</td> : null;
                  if (!completedCarts || !projected?.completedBrt) return planVols ? (
                    <React.Fragment key={c.key}><td style={{ ...colStyle, borderLeft: '1px solid #eee' }}>—</td><td style={colStyle}>—</td><td style={colStyle}></td>{projCpp}</React.Fragment>
                  ) : <React.Fragment key={c.key}><td style={{ ...colStyle, borderLeft: '1px solid #eee' }}>—</td>{projCpp}</React.Fragment>;
                  const rate = completedCarts / projected.completedBrt;
                  // Completed-hours rate projected across the whole shift. Floor it at
                  // the actual-so-far (ALL hours incl. current partial) so the projection
                  // can never display below what's already been produced — which looked
                  // like a bug when the current hour outpaced the completed-hours rate.
                  const actualTotal = data.cartons?.[c.key]?.total || 0;
                  const proj = Math.max(actualTotal, Math.round(rate * projected.totalBrt));
                  // Full-day plan total (all hours, not just completed) for this bucket
                  const planFull = hourLabels.reduce((s, _, i) => s + getPlannedCartons(planVols, c.key, i), 0);
                  return planVols ? (
                    <React.Fragment key={c.key}>
                      <td style={{ ...colStyle, borderLeft: '1px solid #eee', color: '#1565c0' }}>{planFull ? planFull.toLocaleString() : '—'}</td>
                      <td style={{ ...colStyle, color: '#1565c0' }}>{proj.toLocaleString()}</td>
                      <td style={colStyle}></td>
                      {projCpp}
                    </React.Fragment>
                  ) : <React.Fragment key={c.key}><td style={{ ...colStyle, borderLeft: '1px solid #eee', color: '#1565c0' }}>{proj.toLocaleString()}</td>{projCpp}</React.Fragment>;
                })}
                <td style={{ ...colStyle, borderLeft: '1px solid #eee' }}>—</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // LP rates for % to LP calc — LVP plan rates when available, else hardcoded fallback.
  // 'EOL/AROS' fallback (78) is a blend of the old EOL-only (90) and AROS-Outbound
  // (57.2) rates for the fused dept, weighted toward EOL (the larger sort stream);
  // used only in the no-LVP-plan case (the LVP-override path below blends precisely).
  const lvpLpRates = { 'Diluted CPLH': 48, 'Direct CPLH': 55, 'Outbound': 44, 'Inbound': 63, 'PID Dept': 153.1, 'AROS Inbound': 142.8, 'PR Dept': 35.7, 'EOL/AROS': 78, 'Fluid Total': 46.4, 'PIT Total': 124.6,
    // Override with LVP plan rates (planned cartons / planned hours per dept) for this shift
    ...(lvpPlan ? (() => {
      const sk = lvpPlan.shiftKey, ch = lvpPlan.computedHours, c = lvpPlan.cartons;
      const H = (k) => ch?.[k]?.[sk] || 0;
      const r = {};
      // Diluted = cartons*2 / (IB + DA + Admin hrs); Direct = cartons*2 / (IB + DA hrs)
      // — matching the LVP tab's own CPLH definitions, so these track LVP edits
      // instead of the hardcoded 48/55 fallbacks.
      const c2 = (lvpPlan.totalCartons || 0) * 2;
      const dilDen = (lvpPlan.ibHours || 0) + (lvpPlan.daHours || 0) + (lvpPlan.adminHours || 0);
      const dirDen = (lvpPlan.ibHours || 0) + (lvpPlan.daHours || 0);
      if (dilDen > 0) r['Diluted CPLH'] = c2 / dilDen;
      if (dirDen > 0) r['Direct CPLH'] = c2 / dirDen;
      if (H('PID')) r['PID Dept'] = c.pid / H('PID');
      // AROS Inbound: IB AROS functions only (not the whole 'AROS' dept, which
      // still includes OB AROS in LVP). Sum the IB functions' planned hours.
      const arosIbHrs = H('AROS Unload') + H('AROS Operator') + H('AROS CSX Label');
      if (arosIbHrs > 0) r['AROS Inbound'] = c.aros / arosIbHrs;
      if (H('IB PR')) r['PR Dept'] = c.pr / H('IB PR');
      if (H('Fluid')) r['Fluid Total'] = (c.fluidDirect + c.fluidOffline) / H('Fluid');
      // Fused EOL/AROS plan rate: (eol + aros) cartons over the fused planned
      // hours. EOL Sort / AROS Sort are saved under the renamed synthetic keys
      // IDRT EOL / IDRT AROS, so include both names (only one carries hours) —
      // otherwise the two largest lines were dropped and the rate was inflated.
      const eolArosHrs = H('EOL Avery') + H('EOL Divert') + H('EOL Sort') + H('IDRT EOL') + H('EOL Waterspider')
        + H('AROS Avery') + H('AROS Divert') + H('AROS Sort') + H('IDRT AROS') + H('AROS Waterspider');
      if (eolArosHrs > 0) r['EOL/AROS'] = ((c.eol || 0) + (c.aros || 0)) / eolArosHrs;
      if (H('IB PIT')) r['PIT Total'] = c.pit / H('IB PIT');
      return r;
    })() : {})
  };
  // ALPS-planned sites (SBD3/SCK8): only ALPS-derived targets — never the OLM1
  // hardcoded fallbacks above. Depts ALPS doesn't plan show '—' (no % to LP).
  const lpRates = (lvpPlan && lvpPlan.source === 'alps') ? (() => {
    const r = {};
    if (lvpPlan.ibCplh) r['Inbound'] = lvpPlan.ibCplh;
    if (lvpPlan.daCplh) r['Outbound'] = lvpPlan.daCplh;
    const hrs = (lvpPlan.ibHours || 0) + (lvpPlan.daHours || 0);
    const ctn = (lvpPlan.cartons.inbound || 0) + (lvpPlan.cartons.outbound || 0);
    if (hrs > 0 && ctn > 0) r['Direct CPLH'] = ctn / hrs;
    return r;
  })() : lvpLpRates;

  const DeptThroughput = ({ data, hourLabels }) => {
    const colStyle = { textAlign: 'right', padding: '4px 5px', fontSize: '0.72rem' };
    const headerStyle = { ...colStyle, fontWeight: 600, borderBottom: '1px solid #ccc' };

    const depts = [
      { label: 'Diluted CPLH', hoursKey: 'fcSummary', cartonCalc: (h) => (data.cartons?.inbound?.hours?.[h] || 0) + (data.cartons?.outbound?.hours?.[h] || 0) },
      { label: 'Direct CPLH', hoursCalc: (h) => { const fc = data.ppr_hours?.fcSummary?.[h] || 0; const ib = data.ppr_hours?.inbound?.[h] || 0; const ob = data.ppr_hours?.transferOut?.[h] || 0; const support = fc - ib - ob; const direct = fc - (support > 0 ? support : 0); const tot = data.ppr_hours?.tot?.[h] || 0; return direct + tot; }, cartonCalc: (h) => (data.cartons?.inbound?.hours?.[h] || 0) + (data.cartons?.outbound?.hours?.[h] || 0) },
      { label: 'Outbound', hoursKey: 'transferOut', cartonCalc: (h) => data.cartons?.outbound?.hours?.[h] || 0 },
      { label: 'Inbound', hoursKey: 'inbound', cartonCalc: (h) => data.cartons?.inbound?.hours?.[h] || 0 },
      { label: 'PID Dept', deptKey: 'PID Dept', cartonCalc: (h) => data.cartons?.pid?.hours?.[h] || 0 },
      { label: 'AROS Inbound', deptKey: 'AROS Inbound', cartonCalc: (h) => data.cartons?.aros?.hours?.[h] || 0 },
      { label: 'PR Dept', deptKey: 'PR Dept', cartonCalc: (h) => data.cartons?.pr?.hours?.[h] || 0 },
      { label: 'Fluid Total', deptKey: 'Fluid Total', cartonCalc: (h) => (data.cartons?.fluidDirect?.hours?.[h] || 0) + (data.cartons?.fluidOffline?.hours?.[h] || 0) },
      // Fused EOL/AROS department (frontend-only aggregation over the UNCHANGED
      // backend payload). Hours = EOL (Avery/Divert/Waterspider) + OB AROS
      // (Avery/Divert/Waterspider) + Manual Sort, where Manual Sort = the backend's
      // 'EOL Sort' + 'AROS Sort' labor paths summed. Carton basis = eol + aros.
      { label: 'EOL/AROS', deptKey: 'EOL/AROS', hoursCalc: (h) => {
        const eolArosPaths = ['EOL Avery', 'EOL Divert', 'EOL Waterspider', 'EOL Sort', 'AROS Avery', 'AROS Divert', 'AROS Sort', 'AROS Waterspider'];
        let total = 0;
        if (data.labor) {
          for (const l of data.labor) {
            if (eolArosPaths.includes(l.process_path)) total += (l.hours?.[h] || 0);
          }
        }
        return total;
      }, cartonCalc: (h) => (data.cartons?.eol?.hours?.[h] || 0) + (data.cartons?.aros?.hours?.[h] || 0) },
      { label: 'PIT Total', deptKey: 'PIT Total', cartonCalc: (h) => data.cartons?.pit?.hours?.[h] || 0 },
    ].filter(d => !(hideAros && (d.label === 'AROS Inbound' || d.label === 'EOL/AROS')));

    const getHrs = (dept, h) => {
      if (dept.hoursCalc) return dept.hoursCalc(h);
      if (dept.hoursKey) return data.ppr_hours?.[dept.hoursKey]?.[h] || 0;
      return data.dept_throughput?.[dept.deptKey]?.hours?.[h] || 0;
    };
    const getTotalHrs = (dept) => {
      return hourLabels.reduce((s, h) => s + getHrs(dept, h), 0);
    };

    // % to LP: ((BRT × 1.14286) ÷ Paid) × LP Rate
    const calcPctToLP = (dept, hi) => {
      const sched = data.schedule?.[hi];
      if (!sched || !sched.paid) return null;
      const rate = lpRates[dept.label] || 0;
      return ((sched.brt * 1.14286) / sched.paid) * rate;
    };

    // Support-section hours-only columns (front-page PPR Support), rightmost.
    const supportCols = [
      { label: 'Admin Hours', key: 'admin' },
      { label: 'Non_FC_Controllable', key: 'nonFcControllable' },
      { label: 'On Boarding', key: 'onboarding' },
    ];
    const supHr = (key, h) => data.support_hours?.[key]?.[h] || 0;
    const supTotal = (key) => hourLabels.reduce((s, h) => s + supHr(key, h), 0);
    // Time Off Task hours per hour, from the timeOnTask report: sum(Total Time)
    // minus sum(Time On Task) across all employees in the hour window.
    const totHr = (h) => data.ppr_hours?.tot?.[h] || 0;
    const totTotal = () => hourLabels.reduce((s, h) => s + totHr(h), 0);

    return (
      <div style={{ marginBottom: 24, overflowX: 'auto' }}>
        <h3 className="section-title">Department Throughput</h3>
        <table style={{ fontSize: '0.72rem', whiteSpace: 'nowrap', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1 }}></th>
              <th style={{ ...headerStyle, textAlign: 'center', borderLeft: '2px solid #232f3e', background: '#f3e5f5' }}>TOT</th>
              {depts.map(d => (
                <th key={d.label} colSpan={3} style={{ ...headerStyle, textAlign: 'center', borderLeft: '1px solid #ddd' }}>{d.label}</th>
              ))}
              {supportCols.map(c => (
                <th key={c.key} style={{ ...headerStyle, textAlign: 'center', borderLeft: '2px solid #232f3e', background: '#fff3e0' }}>{c.label}</th>
              ))}
            </tr>
            <tr>
              <th style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1 }}></th>
              <th style={{ ...headerStyle, borderLeft: '2px solid #232f3e', background: '#f3e5f5' }}>Hours</th>
              {depts.map(d => (
                <React.Fragment key={d.label}>
                  <th style={{ ...headerStyle, borderLeft: '1px solid #ddd' }}>Hours</th>
                  <th style={headerStyle}>CPLH</th>
                  <th style={headerStyle}>% to LP</th>
                </React.Fragment>
              ))}
              {supportCols.map(c => (
                <th key={c.key} style={{ ...headerStyle, borderLeft: '2px solid #232f3e', background: '#fff3e0' }}>Hours</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 700, background: '#f5f8ff', borderBottom: '2px solid #232f3e' }}>
              <td style={{ position: 'sticky', left: 0, background: '#f5f8ff', zIndex: 1, padding: '4px 8px' }}>Plan</td>
              <td style={{ ...colStyle, borderLeft: '2px solid #232f3e', background: '#f5f8ff' }}>—</td>
              {depts.map(d => {
                const ph = lvpDeptHours(d.label);
                const pr = lpRates[d.label] || 0;
                return (
                  <React.Fragment key={d.label}>
                    <td style={{ ...colStyle, borderLeft: '1px solid #ddd', color: '#283593' }}>{ph ? ph.toFixed(1) : '—'}</td>
                    <td style={{ ...colStyle, color: '#283593' }}>{pr ? fmtCplh(pr) : '—'}</td>
                    <td style={colStyle}>—</td>
                  </React.Fragment>
                );
              })}
              {supportCols.map(c => (
                <td key={c.key} style={{ ...colStyle, borderLeft: '2px solid #232f3e', background: '#f5f8ff' }}>—</td>
              ))}
            </tr>
            {hourLabels.map((h, hi) => (
              <tr key={h}>
                <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, fontWeight: 500, padding: '4px 8px' }}>{h}</td>
                <td style={{ ...colStyle, borderLeft: '2px solid #232f3e', background: '#faf4fc' }}>{totHr(h) ? totHr(h).toFixed(1) : '—'}</td>
                {depts.map(d => {
                  const hrs = getHrs(d, h);
                  const carts = d.cartonCalc(h);
                  const cplh = hrs > 0 ? carts / hrs : 0;
                  const lpTarget = calcPctToLP(d, hi);
                  const pct = lpTarget && cplh > 0 ? Math.round((cplh / lpTarget) * 100) : null;
                  // Match the function tables' colors exactly.
                  const bg = pct == null ? undefined : pct >= 100 ? '#e8f5e9' : pct >= 95 ? undefined : '#ffebee';
                  return (
                    <React.Fragment key={d.label}>
                      <td style={{ ...colStyle, borderLeft: '1px solid #eee', background: bg }}>{hrs ? hrs.toFixed(1) : '—'}</td>
                      <td style={{ ...colStyle, background: bg }}>{cplh ? fmtCplh(cplh) + totFlag(h) : '—'}</td>
                      <td style={{ ...colStyle, background: bg, color: pct && pct >= 100 ? '#2e7d32' : pct ? '#c62828' : '#666' }}>{pct ? `${pct}%` : '—'}</td>
                    </React.Fragment>
                  );
                })}
                {supportCols.map(c => {
                  const hrs = supHr(c.key, h);
                  return <td key={c.key} style={{ ...colStyle, borderLeft: '2px solid #232f3e', background: '#fff8ef' }}>{hrs ? hrs.toFixed(1) : '—'}</td>;
                })}
              </tr>
            ))}
            <tr style={{ fontWeight: 700, borderTop: '2px solid #232f3e' }}>
              <td style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1, padding: '4px 8px' }}>Total</td>
              <td style={{ ...colStyle, borderLeft: '2px solid #232f3e', background: '#f3e5f5' }}>{totTotal() ? totTotal().toFixed(1) : '—'}</td>
              {depts.map(d => {
                const totalHrs = getTotalHrs(d);
                const totalCarts = hourLabels.reduce((s, h) => s + d.cartonCalc(h), 0);
                const cplh = totalHrs > 0 ? totalCarts / totalHrs : 0;
                return (
                  <React.Fragment key={d.label}>
                    <td style={{ ...colStyle, borderLeft: '1px solid #eee' }}>{totalHrs.toFixed(1)}</td>
                    <td style={colStyle}>{cplh ? fmtCplh(cplh) : '—'}</td>
                    <td style={colStyle}></td>
                  </React.Fragment>
                );
              })}
              {supportCols.map(c => {
                const t = supTotal(c.key);
                return <td key={c.key} style={{ ...colStyle, borderLeft: '2px solid #232f3e', background: '#fff3e0' }}>{t ? t.toFixed(1) : '—'}</td>;
              })}
            </tr>
            {projected && projected.completedBrt > 0 && (
              <tr style={{ fontWeight: 500, background: '#e3f2fd', fontStyle: 'italic' }}>
                <td style={{ position: 'sticky', left: 0, background: '#e3f2fd', zIndex: 1, padding: '4px 8px' }}>Proj. Finish</td>
                <td style={{ ...colStyle, borderLeft: '2px solid #232f3e' }}>—</td>
                {depts.map(d => {
                  const totalHrs = getTotalHrs(d);
                  const totalCarts = hourLabels.reduce((s, h) => s + d.cartonCalc(h), 0);
                  if (!totalHrs || !totalCarts) return (
                    <React.Fragment key={d.label}><td style={{ ...colStyle, borderLeft: '1px solid #eee' }}>—</td><td style={colStyle}>—</td><td style={colStyle}></td></React.Fragment>
                  );
                  const projHrs = totalHrs / projected.completedBrt * projected.totalBrt;
                  const projCarts = totalCarts / projected.completedBrt * projected.totalBrt;
                  const projCplh = projHrs > 0 ? projCarts / projHrs : 0;
                  return (
                    <React.Fragment key={d.label}>
                      <td style={{ ...colStyle, borderLeft: '1px solid #eee', color: '#1565c0' }}>{projHrs.toFixed(1)}</td>
                      <td style={{ ...colStyle, color: '#1565c0' }}>{projCplh ? fmtCplh(projCplh) : '—'}</td>
                      <td style={colStyle}></td>
                    </React.Fragment>
                  );
                })}
                {supportCols.map(c => <td key={c.key} style={{ ...colStyle, borderLeft: '2px solid #232f3e' }}></td>)}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const HoursAsRowsNoRate = ({ title, hourLabels }) => {
    const colStyle = { textAlign: 'right', padding: '4px 5px', fontSize: '0.72rem' };
    const headerStyle = { ...colStyle, fontWeight: 600, borderBottom: '1px solid #ccc' };
    const getGroupTotal = (paths, h) => paths.reduce((s, p) => s + (getHours(p)[h] || 0), 0);
    const getGroupGrandTotal = (paths) => paths.reduce((s, p) => s + getTotal(p), 0);
    return (
      <div style={{ marginBottom: 24, overflowX: 'auto' }}>
        <h3 className="section-title">{title}</h3>
        <table style={{ fontSize: '0.72rem', whiteSpace: 'nowrap', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1 }}></th>
              <th style={{ ...headerStyle, textAlign: 'center', background: '#e3f2fd' }}>IB Total</th>
              {ibIndirectPaths.map(p => <th key={p} style={{ ...headerStyle, textAlign: 'center' }}>{p}</th>)}
              <th style={{ ...headerStyle, textAlign: 'center', background: '#fff3e0' }}>OB Total</th>
              {obIndirectPaths.map(p => <th key={p} style={{ ...headerStyle, textAlign: 'center' }}>{p}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 700, background: '#f5f8ff', borderBottom: '2px solid #232f3e' }}>
              <td style={{ position: 'sticky', left: 0, background: '#f5f8ff', zIndex: 1, padding: '4px 8px' }}>Plan</td>
              <td style={{ ...colStyle, color: '#283593', fontWeight: 700 }}>{ibIndirectPaths.reduce((s, p) => s + lvpPathHours(p), 0).toFixed(1)}</td>
              {ibIndirectPaths.map(p => { const ph = lvpPathHours(p); return <td key={p} style={{ ...colStyle, color: '#283593' }}>{ph ? ph.toFixed(1) : '—'}</td>; })}
              <td style={{ ...colStyle, color: '#283593', fontWeight: 700 }}>{obIndirectPaths.reduce((s, p) => s + lvpPathHours(p), 0).toFixed(1)}</td>
              {obIndirectPaths.map(p => { const ph = lvpPathHours(p); return <td key={p} style={{ ...colStyle, color: '#283593' }}>{ph ? ph.toFixed(1) : '—'}</td>; })}
            </tr>
            {hourLabels.map(h => (
              <tr key={h}>
                <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, fontWeight: 500, padding: '4px 8px' }}>{h}</td>
                <td style={{ ...colStyle, background: '#e3f2fd', fontWeight: 600 }}>{getGroupTotal(ibIndirectPaths, h) ? getGroupTotal(ibIndirectPaths, h).toFixed(1) : '—'}</td>
                {ibIndirectPaths.map(p => {
                  const hrs = getHours(p)[h] || 0;
                  return <td key={p} style={colStyle}>{hrs ? hrs.toFixed(1) : '—'}</td>;
                })}
                <td style={{ ...colStyle, background: '#fff3e0', fontWeight: 600 }}>{getGroupTotal(obIndirectPaths, h) ? getGroupTotal(obIndirectPaths, h).toFixed(1) : '—'}</td>
                {obIndirectPaths.map(p => {
                  const hrs = getHours(p)[h] || 0;
                  return <td key={p} style={colStyle}>{hrs ? hrs.toFixed(1) : '—'}</td>;
                })}
              </tr>
            ))}
            <tr style={{ fontWeight: 700, borderTop: '2px solid #232f3e' }}>
              <td style={{ position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1, padding: '4px 8px' }}>Total</td>
              <td style={{ ...colStyle, background: '#e3f2fd' }}>{getGroupGrandTotal(ibIndirectPaths).toFixed(1)}</td>
              {ibIndirectPaths.map(p => <td key={p} style={colStyle}>{getTotal(p) ? getTotal(p).toFixed(1) : '—'}</td>)}
              <td style={{ ...colStyle, background: '#fff3e0' }}>{getGroupGrandTotal(obIndirectPaths).toFixed(1)}</td>
              {obIndirectPaths.map(p => <td key={p} style={colStyle}>{getTotal(p) ? getTotal(p).toFixed(1) : '—'}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Filter hour labels - hide current partial hour if toggle is off
  const now2 = new Date();
  const currentHourLabel = String(now2.getHours()).padStart(2,"0") + ":00";
  const isToday = date === `${now2.getFullYear()}-${String(now2.getMonth()+1).padStart(2,"0")}-${String(now2.getDate()).padStart(2,"0")}`;
  const filteredLabels = data?.hour_labels ? ((!showPartial && isToday) ? data.hour_labels.filter(h => h < currentHourLabel) : data.hour_labels) : [];

  return (
    <div>
      <h2 className="section-title">Hourly — {site}</h2>
      <div className="card">
        <div className="form-row">
          <div className="form-group">
            <label>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Shift Type</label>
            <select value={shiftType} onChange={e => setShiftType(e.target.value)}>
              <option value="day">Day</option>
              <option value="night">Night</option>
              <option value="24hr">24 Hour</option>
            </select>
          </div>
          <div className="form-group" style={{ justifyContent: 'flex-end', gap: 8, display: 'flex' }}>
            <label style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={showPartial} onChange={e => setShowPartial(e.target.checked)} />
              Show Partial Hours
            </label>
            <button className="btn" onClick={fetchCacheOnly} disabled={loading} style={{ background: '#ff9800', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}>
              {loading ? '...' : "I'm feeling lucky 🦞"}
            </button>
            <button className="btn btn-primary" onClick={() => fetchData(true)} disabled={loading}>
              {loading ? 'Fetching...' : 'Refresh'}
            </button>
          </div>
        </div>
        {lastFetch && <p style={{ fontSize: '0.8rem', color: '#666' }}>Last fetched: {new Date(lastFetch).toLocaleString()} | Auto-refreshes hourly at :15</p>}
        {shiftType === '24hr' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Full Day Carton Plan:</label>
            <input type="number" min="0" value={cartonPlanInput} onChange={e => setCartonPlanInput(e.target.value)}
              placeholder="Enter total carton plan"
              style={{ width: 140, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4 }} />
            {cartonPlanInput && data?.cartons?.inbound && (() => {
              const actual = (data.cartons.inbound.total || 0) + (data.cartons.outbound?.total || 0);
              const plan = parseInt(cartonPlanInput) * 2; // IB + OB (1:1)
              const delta = actual - plan;
              const pct = plan > 0 ? Math.round((actual / plan) * 100) : 0;
              return <span style={{ fontSize: '0.85rem', color: delta >= 0 ? '#2e7d32' : '#c62828', fontWeight: 500 }}>
                Actual: {actual.toLocaleString()} | {delta >= 0 ? '+' : ''}{delta.toLocaleString()} ({pct}% to plan)
              </span>;
            })()}
          </div>
        )}
      </div>

      {loading && !data && <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 40 }}>
        <img src={loadingImage()} alt="Loading" style={{ width: 80, height: 80, animation: 'spin 1.5s linear infinite' }} />
        <p style={{ marginTop: 12, fontSize: '0.85rem', color: '#666' }}>Loading FCLM data...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>}

      {data && data.cartons && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <BRTTable hourLabels={filteredLabels} />
        </div>
      )}

      {data && data.ppr_hours && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <DeptThroughput data={data} hourLabels={filteredLabels} />
        </div>
      )}

      {data && data.labor && (
        <>
          <div className="card" style={{ overflowX: 'auto' }}>
            <HoursAsRows title="Inbound LP Functions" paths={ibPaths} hourLabels={filteredLabels} />
          </div>
          <div className="card" style={{ overflowX: 'auto' }}>
            <HoursAsRows title="OB Functions" paths={obPaths} hourLabels={filteredLabels} />
          </div>
          <div className="card" style={{ overflowX: 'auto' }}>
            <HoursAsRowsNoRate title="Non-Rated Indirects" hourLabels={filteredLabels} />
          </div>
        </>
      )}

      {/* Fast Start Analysis */}
      <div className="card">
        <FastStart api={api} site={site} date={date} shiftType={shiftType} />
      </div>

      {data && data.error && <div className="card"><p style={{ color: 'red' }}>Error: {data.error}</p></div>}
    </div>
  );
}
